import React, { useState } from 'react';
import { Route, Routes, Link as RouterLink, useNavigate, Navigate } from 'react-router-dom';
import { AppBar, Toolbar, Typography, Container, Button, Box, IconButton, Menu, MenuItem, ListItemIcon, ListItemText } from '@mui/material';
import AccountCircle from '@mui/icons-material/AccountCircle';
import Brightness4Icon from '@mui/icons-material/Brightness4';
import Brightness7Icon from '@mui/icons-material/Brightness7';
import Login from './pages/Login.tsx';
import Register from './pages/Register.tsx';
import Dashboard from './pages/Dashboard.tsx';
import UserManagement from './pages/UserManagement.tsx';
import ParkingSpotManagement from './pages/ParkingSpotManagement.tsx';
import HistoryPage from './pages/History.tsx';
import ProfilePage from './pages/Profile.tsx';
import ProtectedRoute from './components/ProtectedRoute.tsx';
import { useAuth } from './components/AuthContext.tsx';
import { useThemeContext } from './components/ThemeContext.tsx';

function App() {
  const { user, isAuthenticated, isAdmin, logout, isLoading } = useAuth();
  const { mode, toggleColorMode } = useThemeContext();
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const handleLogout = () => {
    logout();
    handleClose();
    navigate('/login');
  };

  const handleMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  return (
    <>
      <AppBar position="static">
        <Toolbar>
          <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
            Gestion de Parking
          </Typography>
          {isAuthenticated && (
            <Button color="inherit" component={RouterLink} to="/dashboard">Tableau de bord</Button>
          )}
          {isAdmin && (
            <>
              <Button color="inherit" component={RouterLink} to="/users">Utilisateurs</Button>
              <Button color="inherit" component={RouterLink} to="/spots">Places</Button>
              <Button color="inherit" component={RouterLink} to="/history">Historique</Button>
            </>
          )}
          <Box sx={{ flexGrow: 1 }} />
          {isAuthenticated && user ? (
            <div>
              <IconButton
                size="large"
                aria-label="account of current user"
                aria-controls="menu-appbar"
                aria-haspopup="true"
                onClick={handleMenu}
                color="inherit"
              >
                <AccountCircle />
              </IconButton>
              <Menu
                id="menu-appbar"
                anchorEl={anchorEl}
                anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                keepMounted
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                open={Boolean(anchorEl)}
                onClose={handleClose}
              >
                <MenuItem disabled>
                  <ListItemText primary={`${user.first_name} ${user.last_name}`} />
                </MenuItem>
                <MenuItem component={RouterLink} to="/profile" onClick={handleClose}>
                  Mon Profil
                </MenuItem>
                <MenuItem onClick={toggleColorMode}>
                  <ListItemIcon>
                    {mode === 'dark' ? <Brightness7Icon fontSize="small" /> : <Brightness4Icon fontSize="small" />}
                  </ListItemIcon>
                  <ListItemText primary={mode === 'dark' ? 'Thème clair' : 'Thème sombre'} />
                </MenuItem>
                <MenuItem onClick={handleLogout}>Déconnexion</MenuItem>
              </Menu>
            </div>
          ) : ( 
            <>
              <Button color="inherit" component={RouterLink} to="/login">Connexion</Button>
              <Button color="inherit" component={RouterLink} to="/register">Inscription</Button>
            </>
          )}
        </Toolbar>
      </AppBar>
      <Container maxWidth="lg" sx={{ mt: 4 }}>
        <Routes>
          <Route path="/" element={
            isLoading ? null : (isAuthenticated ? <Navigate to="/dashboard" replace /> : <Navigate to="/login" replace />)
          } />
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />

          {/* Routes protégées pour tous les utilisateurs connectés */}
          <Route element={<ProtectedRoute />}>
            <Route path="/dashboard" element={<Dashboard />} />
            <Route path="/profile" element={<ProfilePage />} />
          </Route>

          {/* Routes protégées réservées aux administrateurs */}
          <Route element={<ProtectedRoute isAdminRoute />}>
            <Route path="/users" element={<UserManagement />} />
            <Route path="/spots" element={<ParkingSpotManagement />} />
            <Route path="/history" element={<HistoryPage />} />
          </Route>

          <Route path="*" element={<Typography variant="h5">404 - Page non trouvée</Typography>} />
        </Routes>
      </Container>
    </>
  );
}

export default App;
